import { Link } from "react-router-dom";
import { Settings02Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { LabelPosition, Switch } from "shared/components/forms/Switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { ActiveTab } from "../Table/constants";
import { StageDot, type ApplicationTabItem } from "../ApplicationTabs";
import { tabHiddenKey, type TabValue } from "../../utils/tabs";

const TabSwitchRow = ({
  tab,
  hidden,
  locked,
  onToggle
}: {
  tab: ApplicationTabItem;
  hidden: boolean;
  locked?: boolean;
  onToggle: (tab: TabValue, visible: boolean) => void;
}) => (
  <div className="p-3">
    <Switch
      label={
        <span className="flex items-center gap-2">
          <StageDot color={tab.color} />
          {tab.label}
        </span>
      }
      checked={!hidden}
      disabled={locked}
      onChange={() => onToggle(tab.value, hidden)}
      wrapperClassName="justify-between"
      position={LabelPosition.Left}
    />
  </div>
);

export const EditTabsMenu = ({
  statusTabs,
  stageTabs,
  hiddenTabs,
  onToggleTab,
  disabled = false,
  isMobile,
  canManageStages
}: {
  statusTabs: ApplicationTabItem[];
  stageTabs: ApplicationTabItem[];
  hiddenTabs: string[];
  onToggleTab: (tab: TabValue, visible: boolean) => void;
  disabled?: boolean;
  isMobile: boolean;
  canManageStages: boolean;
}) => {
  const isHidden = (tab: ApplicationTabItem) =>
    tab.value !== ActiveTab.All && hiddenTabs.includes(tabHiddenKey(tab.value));

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger asChild>
        <IconButton
          size={isMobile ? IconButtonSize.BASE : IconButtonSize.SM}
          variant={IconButtonVariant.BASE}
          HugeIcon={Settings02Icon}
          title="Edit tabs"
          aria-label="Edit tabs"
        />
      </PopoverTrigger>

      <PopoverContent className="mt-2 py-5 px-3 z-20 w-[320px] rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
        <h3 className="px-3 pb-2 text-sm text-content-200 font-medium">
          Statuses
        </h3>
        <div className="text-sm">
          {statusTabs.map((tab) => (
            <TabSwitchRow
              key={tab.value}
              tab={tab}
              hidden={isHidden(tab)}
              locked={disabled || tab.value === ActiveTab.All}
              onToggle={onToggleTab}
            />
          ))}
        </div>
        <Show if={stageTabs.length > 0}>
          <div className="mt-2 border-t border-alpha-40 pt-4">
            <h3 className="px-3 pb-2 text-sm text-content-200 font-medium">
              Stages
            </h3>
            <div className="text-sm">
              {stageTabs.map((tab) => (
                <TabSwitchRow
                  key={tab.value}
                  tab={tab}
                  hidden={isHidden(tab)}
                  locked={disabled}
                  onToggle={onToggleTab}
                />
              ))}
            </div>
            <Show if={canManageStages}>
              <div className="px-3 pt-3">
                <Link
                  to="/settings/application"
                  className="text-sm font-bold text-primary-500 hover:text-primary-600"
                >
                  Manage stages
                </Link>
              </div>
            </Show>
          </div>
        </Show>
      </PopoverContent>
    </Popover>
  );
};
